import * as React from 'react'
import { SubscriptionType } from '../../gen/types'
import {
  trackFreeTrialStarted,
  trackStudioAiFreeTrialStarted,
  trackMonthlySubscriptionStarted,
  trackYearlySubscriptionStarted,
  trackStudioAiYearlySubscriptionStarted,
} from '@repo/analytics/google-site-tag'
import {
  CF_ALL_ACCESS_YEARLY_PRICE,
  CF_ALL_ACCESS_MONTHLY_PRICE_USD,
  CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
} from 'constants/prices'
import {
  CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
  CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
} from 'constants/product-ids'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'

let GOOGLE_ADS_PIXEL_SUBSCRIPTION: string | undefined

try {
  GOOGLE_ADS_PIXEL_SUBSCRIPTION =
    process.env.NEXT_PUBLIC_GOOGLE_ADS_PIXEL_SUBSCRIPTION ||
    process.env.VITE_GOOGLE_ADS_PIXEL_SUBSCRIPTION
} catch {}

let GOOGLE_ADS_TAG: string | undefined

try {
  GOOGLE_ADS_TAG = process.env.NEXT_PUBLIC_GOOGLE_ADS_TAG || process.env.VITE_GOOGLE_ADS_TAG
} catch {}

export type ValidSubscriptionType =
  | SubscriptionType.AllAccessMonthly
  | SubscriptionType.AllAccessYearly
  | SubscriptionType.AllAccessYearlyPromo
  | SubscriptionType.AllAccessMonthlyFreeTrial
  | SubscriptionType.AllAccessYearlyFreeTrial
  | SubscriptionType.StudioAiYearly
  | SubscriptionType.StudioAiYearlyFreeTrial

type ValueMap = Record<ValidSubscriptionType, string | number>

const cfValueMap: ValueMap = {
  [SubscriptionType.AllAccessMonthly]: CF_ALL_ACCESS_MONTHLY_PRICE_USD,
  [SubscriptionType.AllAccessYearly]: CF_ALL_ACCESS_YEARLY_PRICE,
  [SubscriptionType.AllAccessYearlyPromo]: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
  [SubscriptionType.AllAccessMonthlyFreeTrial]: 0,
  [SubscriptionType.AllAccessYearlyFreeTrial]: 0,
  [SubscriptionType.StudioAiYearly]: CF_ALL_ACCESS_YEARLY_PROMO_PRICE,
  [SubscriptionType.StudioAiYearlyFreeTrial]: 0,
}

const cfItemIdMap: ValueMap = {
  [SubscriptionType.AllAccessMonthly]: CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
  [SubscriptionType.AllAccessMonthlyFreeTrial]: CF_ALL_ACCESS_MONTHLY_PRODUCT_ID,
  [SubscriptionType.AllAccessYearly]: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
  [SubscriptionType.AllAccessYearlyFreeTrial]: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
  [SubscriptionType.AllAccessYearlyPromo]: CF_ALL_ACCESS_YEARLY_PRODUCT_ID,
  [SubscriptionType.StudioAiYearly]: 'studio_ai_yearly',
  [SubscriptionType.StudioAiYearlyFreeTrial]: 'studio_ai_yearly_free_trial',
}

const subscriptionPlanMap: ValueMap = {
  [SubscriptionType.AllAccessMonthly]: 'Subscription Plan - All Access',
  [SubscriptionType.AllAccessYearly]: 'Subscription Plan - All Access',
  [SubscriptionType.AllAccessYearlyPromo]: 'Subscription Plan - All Access',
  [SubscriptionType.AllAccessMonthlyFreeTrial]: 'Subscription Plan - All Access',
  [SubscriptionType.AllAccessYearlyFreeTrial]: 'Subscription Plan - All Access',
  [SubscriptionType.StudioAiYearly]: 'Subscription Plan - Studio AI',
  [SubscriptionType.StudioAiYearlyFreeTrial]: 'Subscription Plan - Studio AI',
}

const subscriptionNameMap: ValueMap = {
  [SubscriptionType.AllAccessMonthly]: 'All Access',
  [SubscriptionType.AllAccessYearly]: 'All Access',
  [SubscriptionType.AllAccessYearlyPromo]: 'All Access',
  [SubscriptionType.AllAccessMonthlyFreeTrial]: 'All Access',
  [SubscriptionType.AllAccessYearlyFreeTrial]: 'All Access',
  [SubscriptionType.StudioAiYearly]: 'Studio AI',
  [SubscriptionType.StudioAiYearlyFreeTrial]: 'Studio AI',
}

export function isValidSubscriptionType(
  subscriptionType: string | null | undefined,
): subscriptionType is ValidSubscriptionType {
  return Object.values(SubscriptionType).includes(subscriptionType as SubscriptionType)
}

export interface AnalyticsOrderSuccessOptions {
  id: string
  tax: number
  total: number
  userId: string
  subscriptionType: ValidSubscriptionType
  currency: string
}

const isLive = process.env.NEXT_PUBLIC_ENV || process.env.VITE_PUBLIC_ENV

export function useAnalyticsOrderSuccess() {
  function onSuccessForAnalytics(options: AnalyticsOrderSuccessOptions) {
    const valueMap = cfValueMap
    const itemIdMap = cfItemIdMap

    if (typeof window.gtag === 'function' && isLive) {
      const value = options.total - options.tax
      const valueToSend = value === 0 ? value : value / 100

      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_TAG}/${GOOGLE_ADS_PIXEL_SUBSCRIPTION}`,
        value: valueMap[options.subscriptionType],
        currency: 'USD',
      })

      const order = {
        currency: options.currency.toUpperCase(),
        transaction_id: options.id,
        value: valueToSend,
        tax: options.tax === 0 ? options.tax : options.tax / 100,
        items: [
          {
            item_id: itemIdMap[options.subscriptionType],
            item_name: subscriptionPlanMap[options.subscriptionType],
            price: valueToSend,
            quantity: 1,
            item_category1: 'Subscriptions',
            item_category2: [subscriptionNameMap[options.subscriptionType]],
          },
          // we need to mutate this object later, so we need to loosely type it
        ] as Record<string, unknown>[],
      }

      const isFreeTrial = value === 0
      if (isFreeTrial) {
        order.items[0].item_variant =
          options.subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial
            ? 'Studio AI Free Trial'
            : 'All Access Free Trial'
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', order)
      }
    }

    if (typeof window.gtag === 'function') {
      const value = options.total - options.tax
      const isFreeTrial = value === 0
      if (isFreeTrial) {
        if (options.subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial) {
          trackStudioAiFreeTrialStarted()
        } else {
          trackFreeTrialStarted()
        }
      }

      switch (options.subscriptionType) {
        case SubscriptionType.AllAccessMonthly: {
          trackMonthlySubscriptionStarted()
          break
        }
        case SubscriptionType.AllAccessYearly:
        case SubscriptionType.AllAccessYearlyPromo: {
          trackYearlySubscriptionStarted()
          break
        }
        case SubscriptionType.StudioAiYearly: {
          trackStudioAiYearlySubscriptionStarted()
          break
        }
      }
    }

    // sadly no types for bing :(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const uetq = (window as any).uetq

    if (uetq) {
      const transactionId = options.id

      if (!transactionId) {
        return
      }

      const ecommProdId = itemIdMap[options.subscriptionType]
      const totalValue = options.total - options.tax
      const totalToSend = totalValue === 0 ? totalValue : totalValue / 100

      uetq.push('event', 'purchase', {
        transaction_id: transactionId,
        ecomm_prodid: [ecommProdId],
        ecomm_pagetype: 'purchase',
        ecomm_totalvalue: totalToSend,
        revenue_value: totalToSend,
        currency: options.currency.toUpperCase(),
        items: [{ id: ecommProdId, quantity: 1, price: totalToSend }],
      })

      uetq.push('event', 'started', {
        event_category: 'subscription',
        revenue_value: totalToSend,
        currency: options.currency.toUpperCase(),
      })
    }

    // no types for fbq :(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fbq = (window as any).fbq

    if (typeof fbq === 'function') {
      const value = String(valueMap[options.subscriptionType])
      const currency = 'USD'

      fbq('track', 'Subscribe', {
        value,
        currency,
      })
    }

    //Amplitude event
    sendAmplitudeEvent(AmplitudeEvents.WELCOME_POPUP_VIEWED, {
      type:
        options.subscriptionType === SubscriptionType.StudioAiYearly ||
        options.subscriptionType === SubscriptionType.StudioAiYearlyFreeTrial
          ? 'studio_ai_trial'
          : 'all_access_trial',
    })
  }

  return { onSuccessForAnalytics: React.useCallback(onSuccessForAnalytics, []) }
}
